import classNames from 'classnames';
import moment from 'moment';
import React, { useCallback } from 'react';
import { FaEuroSign, FaPercent } from 'react-icons/fa';
import { Link, useParams } from 'react-router-dom';
import { Input, Text, Textarea } from 'rizzui';
import CustomSelect from '../../../../../components/forms/CustomSelect';
import FormLabel from '../../../../../components/forms/FormLabel';
import useFetch from '../../../../../hooks/useFetch';
import FormGroup from '../../../../../layout/shared/form-group';
import { menuRoutes } from '../../../../../router/menu';
import { InvoiceService } from '../../../../../services/invoice/invoiceService';
import { InvoiceApiResponse, InvoiceLine } from '../../../../../type/entities/invoice-type';
import { fixNumber } from '../../../../../utils/formatNumber';
import { IVA_NON_SUBJECT_LEGAL_TEXT } from '../../../utils/ivaTerritory';
import InvoiceProfileLayout from '../../InvoiceProfileLayout';
import { usePrivilege } from '../../../../../components/priviledge/PriviledgeProvider';

interface InvoiceInfoProps { }

const getInvoiceSummary = (invoice: any) => {
    if (!invoice) return null;

    return {
        id: invoice.id,
        number: invoice.number,
        totalPrice: invoice.totalPrice,
        invoiceType: invoice.invoiceType,
    };
};

const isSameInvoice = (invoice: any, invoiceId: string) => {
    return Boolean(invoice?.id && invoice?.id === invoiceId);
};

const RECTIFICATION_OPERATION_LABELS: Record<string, string> = {
    minor_regeneration: 'Regeneración de factura',
    full_credit_note: 'Abono completo',
    partial_credit_note: 'Abono parcial',
    full_credit_note_with_replacement: 'Abono completo con factura sustitutiva',
    partial_credit_note_with_replacement: 'Abono parcial con factura sustitutiva',
    major_replacement: 'Abono con factura sustitutiva',
};

const getRectificationOperationLabel = (operationType?: string | null) => {
    if (!operationType) return '-';

    return RECTIFICATION_OPERATION_LABELS[operationType] ?? operationType;
};

const InvoiceRelationCard = ({ title, invoice }: { title: string; invoice: any }) => {
    if (!invoice) {
        return (
            <div className="rounded-md border border-dashed border-gray-200 bg-gray-50 p-3">
                <Text className="text-xs font-semibold uppercase text-gray-500">{title}</Text>
                <Text className="text-sm text-gray-400">Sin documento asociado</Text>
            </div>
        );
    }

    return (
        <div className="rounded-md border border-gray-200 bg-gray-50 p-3">
            <Text className="text-xs font-semibold uppercase text-gray-500">{title}</Text>
            <Link
                to={`${menuRoutes.invoices.path}/${invoice.id}/profile${menuRoutes.invoices.profile.info}`}
                className="block text-sm font-semibold text-primary hover:underline"
            >
                {invoice.number}
            </Link>
            <Text className="text-sm text-gray-600">{fixNumber(invoice.totalPrice || 0)} €</Text>
        </div>
    );
};

const InvoiceInfo: React.FC<InvoiceInfoProps> = () => {

    const { id = '' } = useParams<{ id: string }>();
    const { userCan } = usePrivilege();
    const canViewContract = userCan('get_contracts', 'contracts');
    const canViewClient = userCan('get_clients', 'clients');
    const canViewTutor = userCan('get_tutors', 'tutors');

    const [data, loading] = useFetch(useCallback(async () => {
        if (!id || id === '') return null;
        const response = await (new InvoiceService()).getInvoiceById(id);
        return response.getResponseData() as InvoiceApiResponse;
    }, [id]));

    const [rectificationsData] = useFetch(useCallback(async () => {
        if (!id || id === '') return null;
        const response = await (new InvoiceService()).getInvoiceRectifications(id);
        return response.getResponseData();
    }, [id]));

    const rectifications = Array.isArray(rectificationsData) ? rectificationsData : [];
    const invoiceData = (data as any)?.data ?? data;
    const rectificationSource = rectifications.find((rectification: any) => {
        return isSameInvoice(rectification.creditNoteInvoice, id) || isSameInvoice(rectification.replacementInvoice, id);
    });
    const rectificationsCreatedFromInvoice = rectifications.filter((rectification: any) => {
        return isSameInvoice(rectification.originalInvoice, id);
    });
    const isGeneratedFromRectification = isSameInvoice(rectificationSource?.creditNoteInvoice, id)
        || isSameInvoice(rectificationSource?.replacementInvoice, id)
        || isSameInvoice(invoiceData?.sourceRectification?.creditNoteInvoice, id)
        || isSameInvoice(invoiceData?.sourceRectification?.replacementInvoice, id);
    const originalInvoiceSource = isGeneratedFromRectification
        ? getInvoiceSummary(rectificationSource?.originalInvoice ?? invoiceData?.sourceRectification?.originalInvoice)
        : null;
    const firstActiveExpiration = data?.expirations?.find((expiration: any) => !expiration?.isDeleted);
    const paymentMethodName = data?.paymentMethod?.name
        || data?.contract?.paymentMethods?.name
        || firstActiveExpiration?.paymentMethod?.name
        || '';

    return (
        <InvoiceProfileLayout isLoading={loading}>
            <div className="@container">
                <div className="grid gap-7 divide-y-2 divide-dashed divide-gray-200">
                    <FormGroup
                        title="Información General"
                        description="Datos principales de la factura"
                        className='pt-3 pb-4'
                        titleCols="@md:col-span-2"
                        childCols="@md:col-span-10 md:grid-cols-6"
                    >
                        <Input
                            readOnly
                            id='taxableAmount'
                            type="number"
                            label="Base Imponible"
                            suffix={<FaEuroSign />}
                            value={fixNumber(data?.taxableAmount)}
                        />

                        <Input
                            readOnly
                            id='tax'
                            type="number"
                            label="Impuesto"
                            suffix={<FaPercent />}
                            value={data?.tax}
                        />

                        <Input
                            readOnly
                            id='totalTaxesAmount'
                            type="number"
                            label="Total Impuestos"
                            suffix={<FaEuroSign />}
                            value={fixNumber(data?.totalTaxesAmount)}
                        />

                        <Input
                            readOnly
                            id='totalProducts'
                            type="number"
                            label="Total Productos"
                            value={data?.totalProducts}
                        />

                        <Input
                            readOnly
                            id='totalPrice'
                            type="number"
                            label="Precio Total"
                            suffix={<FaEuroSign />}
                            value={fixNumber(data?.totalPrice)}
                        />

                        {data?.ivaExempt && (
                            <div className="md:col-span-6 rounded border border-amber-200 bg-amber-50 p-3 text-sm text-amber-800">
                                <span className="font-medium">Anotación legal IVA: </span>
                                {IVA_NON_SUBJECT_LEGAL_TEXT}
                            </div>
                        )}

                        {canViewContract ? (
                            <div className="">
                                <FormLabel label='Contrato' />
                                <Link
                                    to={`${menuRoutes.contracts.path}/${data?.contract?.id}/profile/info`}
                                    className="text-primary btn-sm w-full md:w-auto"
                                >
                                    {data?.contract?.code}
                                </Link>
                            </div>
                        ) : (
                            <Input
                                readOnly
                                id='contract'
                                type="text"
                                label="Contrato"
                                value={data?.contract?.code}
                            />
                        )}
                    </FormGroup>

                    <FormGroup
                        title="Empresas y Usuarios"
                        description="Datos de las empresas fiscales y usuarios relacionados"
                        className='pt-3 pb-4'
                        titleCols="@md:col-span-2"
                        childCols="@md:col-span-10 md:grid-cols-2"
                    >
                        <CustomSelect
                            readOnly
                            id={'corporationId'}
                            label="Empresa Fiscal"
                            value={{ value: data?.corporation?.id, label: data?.corporation?.name }}
                        />

                        <CustomSelect
                            readOnly
                            id={'providingCorporationId'}
                            label="Empresa Proveedora"
                            value={{ value: data?.providingCorporation?.id, label: data?.providingCorporation?.name }}
                        />

                        {canViewTutor ? (
                            <div className="">
                                <FormLabel label='Tutor' />
                                <Link
                                    to={`${menuRoutes.tutors.path}/${data?.tutor?.id}/profile/info`}
                                    className="text-primary btn-sm w-full md:w-auto"
                            >
                                {data?.tutor ? data?.tutor?.name + (data?.tutor?.lastName ? ` ${data?.tutor?.lastName}` : '') : ''}
                            </Link>
                        </div>
                        ) : (
                            <Input
                                readOnly
                                id='tutor'
                                type="text"
                                label="Tutor"
                                value={data?.tutor ? data?.tutor?.name + (data?.tutor?.lastName ? ` ${data?.tutor?.lastName}` : '') : ''}
                            />
                        )}

                        {canViewClient ? (
                            <div className="">
                                <FormLabel label='Cliente' />
                                <Link
                                    to={`${menuRoutes.clients.path}/${data?.client?.id}/profile/info`}
                                    className="text-primary btn-sm w-full md:w-auto"
                            >
                                {data?.client ? `${data?.client?.companyName} (${data?.client?.cifNif})` : ''}
                            </Link>
                        </div>
                        ) : (
                            <Input
                                readOnly
                                id='client'
                                type="text"
                                label="Cliente"
                                value={data?.client ? `${data?.client?.companyName} (${data?.client?.cifNif})` : ''}
                            />
                        )}
                    </FormGroup>

                    {data?.lastInvoiceKoReason && (
                        <FormGroup
                            title="Motivo de KO"
                            description="Motivo de KO de la factura"
                            className='pt-3 pb-4'
                            titleCols="@md:col-span-2"
                            childCols="@md:col-span-10 md:grid-cols-2"
                        >
                            <Input
                                readOnly
                                id='lastInvoiceKoReasonName'
                                label="Motivo de KO"
                                value={data.lastInvoiceKoReason.name}
                            />
                            <Textarea
                                readOnly
                                id='lastInvoiceKoReasonDescription'
                                label="Descripción del motivo de KO"
                                value={data.lastInvoiceKoReason.description ?? ''}
                                rows={4}
                                className="md:col-span-2"
                            />
                        </FormGroup>
                    )}

                    {originalInvoiceSource && (
                        <FormGroup
                            title="Factura original"
                            description="Factura original relacionada con este documento"
                            className='pt-3 pb-4'
                            titleCols="@md:col-span-2"
                            childCols="@md:col-span-10"
                        >
                            <div className="max-w-sm">
                                <InvoiceRelationCard title="Procede de" invoice={originalInvoiceSource} />
                            </div>
                        </FormGroup>
                    )}

                    {data?.expirations?.length > 0 && (
                        <FormGroup
                            title="Vencimientos"
                            description="Resumen de pago de los vencimientos"
                            className='pt-3 pb-4'
                            titleCols="@md:col-span-2"
                            childCols="@md:col-span-10"
                        >
                            <div className="space-y-3">
                                {data.expirations.map((expiration: any) => {
                                    const sourceExpirationIds = expiration.sourceExpirationIds ?? [];

                                    return (
                                        <div key={expiration.id} className="rounded-md border border-gray-200 bg-white p-4">
                                            <div className="flex flex-col gap-2 md:flex-row md:items-center md:justify-between">
                                                <div>
                                                    <Text className="font-semibold text-gray-900">
                                                        {expiration.description}
                                                    </Text>
                                                    <Text className="text-sm text-gray-500">
                                                        {expiration.expirationDate?.date
                                                            ? moment(expiration.expirationDate.date).format('DD/MM/YYYY')
                                                            : '-'} · {fixNumber(expiration.amount || 0)} €
                                                    </Text>
                                                    {sourceExpirationIds.length > 0 && (
                                                        <Text className="text-xs text-gray-500">
                                                            Vinculado a {sourceExpirationIds.length} vencimiento{sourceExpirationIds.length > 1 ? 's' : ''} original{sourceExpirationIds.length > 1 ? 'es' : ''}
                                                        </Text>
                                                    )}
                                                </div>
                                                <Text className={classNames(
                                                    'rounded px-2 py-1 text-sm font-semibold',
                                                    expiration.isPaid ? 'bg-green-50 text-green-700' : 'bg-amber-50 text-amber-700'
                                                )}>
                                                    {expiration.isPaid ? 'Pagado' : 'Pendiente'}
                                                </Text>
                                            </div>
                                        </div>
                                    );
                                })}
                            </div>
                        </FormGroup>
                    )}

                    {rectificationsCreatedFromInvoice.length > 0 && (
                        <FormGroup
                            title="Rectificaciones"
                            description="Relación entre factura original, abonos y facturas sustitutivas"
                            className='pt-3 pb-4'
                            titleCols="@md:col-span-2"
                            childCols="@md:col-span-10"
                        >
                            <div className="space-y-3">
                                {rectificationsCreatedFromInvoice.map((rectification: any) => {
                                    const originalInvoice = getInvoiceSummary(rectification.originalInvoice);
                                    const creditNoteInvoice = getInvoiceSummary(rectification.creditNoteInvoice);
                                    const replacementInvoice = getInvoiceSummary(rectification.replacementInvoice);

                                    return (
                                        <div key={rectification.id} className="rounded-md border border-gray-200 bg-white p-4">
                                            <div className="mb-3 flex flex-col gap-1 md:flex-row md:items-center md:justify-between">
                                                <div>
                                                    <Text className="font-semibold text-gray-900">
                                                        {getRectificationOperationLabel(rectification.operationType)}
                                                    </Text>
                                                    <Text className="text-sm text-gray-500">
                                                        {rectification.reason}
                                                    </Text>
                                                </div>
                                                <Text className="text-sm text-gray-500">
                                                    {rectification.operationDate?.date
                                                        ? moment(rectification.operationDate.date).format('DD/MM/YYYY HH:mm')
                                                        : (rectification.operationDate ? moment(rectification.operationDate).format('DD/MM/YYYY HH:mm') : '')}
                                                </Text>
                                            </div>

                                            <div className="grid grid-cols-1 gap-3 md:grid-cols-3">
                                                <InvoiceRelationCard title="Original" invoice={originalInvoice} />
                                                <InvoiceRelationCard title="Abono" invoice={creditNoteInvoice} />
                                                <InvoiceRelationCard title="Sustitutiva" invoice={replacementInvoice} />
                                            </div>
                                        </div>
                                    );
                                })}
                            </div>
                        </FormGroup>
                    )}

                    <FormGroup
                        title="Fechas y Plazos"
                        description="Fechas de la factura y plazos de pago"
                        className='pt-3 pb-4'
                        titleCols="@md:col-span-2"
                        childCols="@md:col-span-10 md:grid-cols-4"
                    >
                        <Input
                            readOnly
                            id='invoiceDate'
                            type="date"
                            label="Fecha de Factura"
                            value={moment(data?.invoiceDate?.date).format('YYYY-MM-DD')}
                        />

                        <Input
                            readOnly
                            id='paymentDate'
                            type="date"
                            label="Fecha de Pago"
                            value={moment(data?.paymentDate?.date).format('YYYY-MM-DD')}
                        />

                        <Input
                            readOnly
                            id='expirationDate'
                            type="date"
                            label="Fecha de Vencimiento"
                            value={moment(data?.expirationDate?.date).format('YYYY-MM-DD')}
                        />

                        <Input
                            readOnly
                            id='paymentMethod'
                            type="text"
                            label="Método de pago"
                            value={paymentMethodName}
                        />

                    </FormGroup>

                    <FormGroup
                        title="Líneas de Factura"
                        description="Líneas de la factura"
                        className='mt-3 pt-6 pb-4 border-top border-t-2 border-dashed'
                        titleCols="@md:col-span-2"
                        childCols="@md:col-span-10"
                        childClassName={classNames('divide-y-2 divide-dashed pb-4', { 'border-gray-500': data?.lines?.length > 0 })}
                    >
                        <div className={classNames('divide-y-2 divide-dashed pb-4', { 'border-gray-500': data?.lines?.length > 0 })}>
                            {data?.lines?.map((item: InvoiceLine, index: number) => {
                                return (
                                    <div key={index} className="flex flex-col md:flex-row flex-nowrap gap-4 mb-5 pt-5">
                                        <div className="grid grid-cols-7 gap-3">
                                            <CustomSelect
                                                readOnly
                                                id={`lines[${index}].contract`}
                                                label='Contrato'
                                                value={item.contract ? { value: item.contract?.id, label: `${item.contract?.code} - ${item.contract?.clientCompanyName}` } : null}
                                                containerClassName='col-span-2'
                                            />
                                            <Input
                                                readOnly
                                                id={`lines[${index}].concept`}
                                                type="text"
                                                label="Concepto"
                                                value={item.concept}
                                            />
                                            <Input
                                                readOnly
                                                id={`lines[${index}].amount`}
                                                label="Cantidad"
                                                inputClassName="text-sm"
                                                value={item.amount}
                                                suffix={<FaEuroSign />}
                                            />
                                            <Input
                                                readOnly
                                                id={`lines[${index}].units`}
                                                label="Unidades"
                                                inputClassName="text-sm"
                                                value={item.units}
                                            />
                                            <Input
                                                readOnly
                                                id={`lines[${index}].totalWithoutTax`}
                                                label="Total sin IVA"
                                                inputClassName="text-sm"
                                                value={item.totalWithoutTax}
                                                suffix={<FaEuroSign />}
                                            />
                                            <Input
                                                readOnly
                                                id={`lines[${index}].totalWithTax`}
                                                label="Total con IVA"
                                                inputClassName="text-sm"
                                                value={item.totalWithTax}
                                                suffix={<FaEuroSign />}
                                            />
                                        </div>
                                    </div>
                                )
                            })}
                        </div>
                    </FormGroup>
                </div>
            </div>
        </InvoiceProfileLayout>
    );
};

export default InvoiceInfo;
